/**
 * Evacuation Logistics panel (§4.4) — the incident-scoped surface operator-cop-
 * doctrine calls "the demo-winning Logistics value" (§2.5 Logistics / §4.4 flood
 * air-evac). It ASSEMBLES existing objects (no new map layers, no new data) into
 * one read view the Logistics section uses to coordinate a northern air-evac:
 *
 *   (a) linked communities with latest headcount + wellness status, fly-to each
 *   (b) aggregate evacuated-vs-remaining progress bar (ICS-209 block 31, live)
 *   (c) staging areas on the incident with staged-resource counts (§4.18)
 *   (d) nearby aviation bases + live aircraft within reach, airlift hulls flagged
 *   (e) active CAP alerts overlapping the linked communities (road-washout/weather)
 *
 * Surfaced from the community / fire / hotspot detail panels (gated to flood / ice
 * / multi-hazard incidents). Ambient register — never pulses; it is coordination
 * context, not an alarm. One GET (/api/incidents/:id/evacuation) backs it.
 */
import { useEffect, useState } from 'react';
import type {
  AircraftFeature,
  EvacuationSurface,
  ResponseAssetFeature,
} from '@tsr/shared-types';
import { fetchEvacuationSurface, fetchResponseAsset, fetchAircraftDetail } from './api';
import { lifecycleStateStyle } from './styles/lifecycleStates';
import { communityStatusStyle } from './styles/communityStatus';
import { logAction } from './lib/operatorActions';
import {
  EVAC_ACCOUNTING_INCOMPLETE_PREFIX,
  EVAC_ACCOUNTING_COMPLETE_LABEL,
  EVAC_ACCOUNTING_PENDING_LABEL,
  EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT,
  EVAC_ACCOUNTING_REMAINING_SHORT,
  EVAC_ACCOUNTING_UNACCOUNTED_SHORT,
} from './incidentVocabulary';

/**
 * Data-driven gate for the Evacuation Logistics entry (CTO ruling 2026-06-04 —
 * replaces the old flood/ice/multi hazard-type gate). The button shows on ANY
 * incident that has ≥1 linked community carrying headcount/wellness data,
 * regardless of hazard: a northern WILDFIRE evac qualifies on the evidence, while
 * a quiet wildfire with no linked community doesn't. Linkage is the same rule the
 * §4.30 surface assembles on — a community has an incident-tied headcount or
 * wellness check (`incident.linked_community_count`).
 */
export function hasEvacuationEvidence(linkedCommunityCount: number | null | undefined): boolean {
  return (linkedCommunityCount ?? 0) >= 1;
}

const HAZARD_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice Storm',
  cap_warning: 'Public Alert',
  multi_hazard: 'Multi-Hazard',
  other: 'Incident',
};

const COMMUNITY_TYPE_LABEL: Record<string, string> = {
  municipality: 'Municipality',
  first_nation: 'First Nation',
  unincorporated: 'Unincorporated',
  metis_settlement: 'Métis Settlement',
  inuit_community: 'Inuit Community',
  other: 'Community',
};

function fmt(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString();
}

/**
 * #462 (gate fix) — the honest awaiting-flight rollup string (UNKNOWN ≠ ZERO). `known` is
 * the sum of the recorded airlift splits; `unknownCount` is how many counted communities
 * still have people remaining but no split recorded. When unknownCount > 0 the known sum is
 * a FLOOR, so we annotate "· N unknown" and NEVER render a bare "0" (which would read as
 * "nobody awaiting a flight" — the exact false all-clear this feature forbids). Exported so
 * the order-panel accounting section renders the airlift figure identically.
 */
export function awaitingAirliftDisplay(known: number, unknownCount: number): string {
  if (unknownCount <= 0) return known.toLocaleString();
  const suffix = ` · ${unknownCount.toLocaleString()} unknown`;
  return (known > 0 ? known.toLocaleString() : '—') + suffix;
}

function relExpiry(iso: string | null): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `expires in ${h}h`;
  return `expires in ${Math.max(1, Math.floor(ms / 60_000))}m`;
}

/**
 * Evacuated-vs-remaining progress bar — the live evacuation-accounting rollup (§4.30).
 * The denominator is evacuated + remaining (people the headcount accounts for as out or
 * still in). Exported for the Ontario IMS vocabulary lock (#499): the rendered caption is
 * an operator surface and must stay free of US-ICS form names.
 *
 * #462 — UNKNOWN ≠ ZERO (life-safety honesty, the #325 class). `pendingCount` is how many
 * in-scope communities are in an evacuation zone for this incident but have NO recorded
 * headcount yet. When it is > 0 the accounting is INCOMPLETE — the bar renders an explicit
 * "N not yet counted — accounting incomplete" banner, NEVER a "complete / all accounted for"
 * affordance, and the bar can never visually read full (an "unknown" tail is always shown).
 * The genuine "complete" affordance is reachable ONLY when pendingCount === 0 AND remaining
 * === 0 AND someone was evacuated — a real all-clear, proven non-vacuous both ways in
 * EvacuationBody.pending.test.tsx.
 */
/** UTC HH:MM for the #647 zero-remaining count age. */
function evacFormatHm(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  return new Date(t).toISOString().slice(11, 16);
}

export function EvacProgressBar({
  evacuated,
  remaining,
  pendingCount = 0,
  countedAsOf = null,
}: {
  evacuated: number;
  remaining: number;
  /** #462 — in-scope communities not yet counted (accounting_state 'pending'). */
  pendingCount?: number;
  /** #647 — the latest headcount time across counted communities; stamps the zero-remaining
   *  fact ("— counted HH:MM") so it reads as an OBSERVATION, not a claim of completion. */
  countedAsOf?: string | null;
}): JSX.Element {
  const denom = evacuated + remaining;
  const rawPct = denom > 0 ? Math.round((evacuated / denom) * 100) : 0;
  const incomplete = pendingCount > 0;
  // When there are uncounted communities the bar must NEVER read full — cap the filled
  // width and always leave a visible "unknown" tail so 100% can't imply an all-clear.
  const fillPct = incomplete ? Math.min(rawPct, 90) : rawPct;
  const complete = !incomplete && remaining === 0 && evacuated > 0;
  return (
    <div data-testid="evac-progress">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-semibold text-rose-200">
          {evacuated.toLocaleString()} evacuated
        </span>
        <span className="text-xs text-slate-400">{remaining.toLocaleString()} remaining</span>
      </div>
      <div
        className={`h-3 w-full rounded-full overflow-hidden ${
          incomplete
            ? 'bg-[repeating-linear-gradient(45deg,rgba(148,163,184,0.25)_0,rgba(148,163,184,0.25)_4px,rgba(51,65,85,0.6)_4px,rgba(51,65,85,0.6)_8px)]'
            : 'bg-slate-700/70'
        }`}
        role="progressbar"
        aria-valuenow={fillPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full bg-rose-500/80 transition-all" style={{ width: `${fillPct}%` }} />
      </div>
      {incomplete ? (
        <p className="mt-1 text-[10px] text-amber-300" data-testid="evac-progress-incomplete">
          {pendingCount.toLocaleString()}{' '}
          {pendingCount === 1 ? 'community' : 'communities'}{' '}
          {EVAC_ACCOUNTING_INCOMPLETE_PREFIX}
        </p>
      ) : complete ? (
        // #647 — the OBSERVED fact, not an all-clear. Neutral slate (never emerald/green): the
        // COP states "0 remaining, counted <HH:MM>", never "complete / all accounted for" — the
        // last plane leaving is not proof of arrival. The gate (zero pending + zero remaining) is
        // unchanged; only the words + colour change.
        <p className="mt-1 text-[10px] text-slate-300" data-testid="evac-progress-complete">
          {EVAC_ACCOUNTING_COMPLETE_LABEL}
          {countedAsOf ? ` — counted ${evacFormatHm(countedAsOf)}` : ''}
        </p>
      ) : (
        <p className="mt-1 text-[10px] text-slate-500">
          {rawPct}% of {denom.toLocaleString()} accounted in scope evacuated
        </p>
      )}
    </div>
  );
}

export function EvacuationBody({
  incidentId,
  onFlyToIncident,
  onFlyToCommunity,
  onSelectAsset,
  onSelectAircraft,
}: {
  incidentId: string;
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** Fly the camera to a linked community centroid (reuses the incident fly). */
  onFlyToCommunity?: (lat: number, lon: number) => void;
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  onSelectAircraft?: (a: AircraftFeature) => void;
}): JSX.Element {
  const [surface, setSurface] = useState<EvacuationSurface | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    setSurface(null);
    setError(false);
    void fetchEvacuationSurface(incidentId, ctl.signal)
      .then((s) => {
        setSurface(s);
        logAction('view_evacuation_logistics', {
          incident_id: incidentId,
          incident_number: s.incident.incident_number,
          hazard_type: s.incident.hazard_type,
          community_count: s.aggregate.community_count,
          total_evacuated: s.aggregate.total_evacuated,
        });
      })
      .catch(() => {
        if (!ctl.signal.aborted) setError(true);
      });
    return () => ctl.abort();
  }, [incidentId]);

  const flyToCommunity = (community: { id: string; name: string; lat: number; lon: number }): void => {
    logAction('fly_to_community', { incident_id: incidentId, community_id: community.id });
    onFlyToCommunity?.(community.lat, community.lon);
  };

  const openAsset = (id: string): void => {
    void fetchResponseAsset(id)
      .then((feat) => {
        if (feat && feat.geometry) onSelectAsset?.(feat as unknown as ResponseAssetFeature);
      })
      .catch(() => undefined);
  };
  const openAircraft = (id: string): void => {
    void fetchAircraftDetail(id)
      .then((feat) => {
        if (feat && feat.geometry) onSelectAircraft?.(feat as unknown as AircraftFeature);
      })
      .catch(() => undefined);
  };

  if (error) {
    return (
      <div className="px-4 py-6 text-sm text-slate-400" data-testid="evacuation-body">
        Could not load the evacuation surface for this incident.
      </div>
    );
  }
  if (!surface) {
    return (
      <div className="px-4 py-6 text-sm text-slate-500" data-testid="evacuation-body">
        Assembling evacuation logistics…
      </div>
    );
  }

  const { incident, communities, aggregate, staging_areas, aviation_bases, aircraft, cap_alerts } =
    surface;
  const airliftCount = aircraft.filter((a) => a.is_airlift).length;
  // #647 — the latest incident-tied headcount time across COUNTED communities, to stamp the
  // zero-remaining fact ("counted <HH:MM>"). Only 'counted' communities carry a real time; a
  // baseline / pending community contributes none (unknown ≠ a fresh count).
  const latestCountedAsOf =
    communities
      .filter((c) => c.accounting_state === 'counted' && c.headcount_as_of)
      .map((c) => c.headcount_as_of as string)
      .sort()
      .at(-1) ?? null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="evacuation-body">
      {/* Header */}
      <div className="border-l-2 border-rose-400 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Evacuation Logistics</p>
        <h2 className="text-base font-semibold">{incident.name}</h2>
        <div className="flex items-center gap-2 mt-1">
          <span className="px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-200 text-[10px]">
            {HAZARD_LABEL[incident.hazard_type] ?? incident.hazard_type}
          </span>
          <span className="font-mono text-xs text-slate-400">{incident.incident_number}</span>
          <button
            type="button"
            data-testid="evac-fly-incident"
            onClick={() => onFlyToIncident?.(incident.lat, incident.lon)}
            className="ml-auto text-[11px] py-1 px-2 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200"
          >
            Fly to incident
          </button>
        </div>
      </div>

      {/* (b) Aggregate evacuated-vs-remaining progress + the honest unknown≠zero rollup */}
      <section className="rounded border border-slate-700 bg-slate-800/40 p-3 space-y-2">
        <EvacProgressBar
          evacuated={aggregate.total_evacuated}
          remaining={aggregate.total_remaining}
          pendingCount={aggregate.pending_community_count}
          countedAsOf={latestCountedAsOf}
        />
        {/* Awaiting flight — the northern air-evac headline the FN air-evac seat reads.
            UNKNOWN ≠ ZERO: when counted communities have people remaining but no recorded
            airlift split, the total is a FLOOR — annotated "N unknown", never a bare 0. */}
        <div
          className="flex items-baseline justify-between rounded bg-indigo-500/10 border border-indigo-500/30 px-2 py-1"
          data-testid="evac-awaiting-airlift"
        >
          <span className="text-[10px] uppercase tracking-wide text-indigo-200/80">
            {EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT}
          </span>
          <span className="font-mono text-sm text-indigo-200" data-testid="evac-awaiting-airlift-value">
            {awaitingAirliftDisplay(
              aggregate.total_awaiting_airlift,
              aggregate.awaiting_airlift_unknown_count,
            )}
          </span>
        </div>
        <dl className="grid grid-cols-4 gap-1 text-center pt-1 border-t border-slate-700/60">
          {(
            [
              ['Acct', aggregate.total_accounted_for, 'text-emerald-300'],
              [EVAC_ACCOUNTING_UNACCOUNTED_SHORT, aggregate.total_unreachable, 'text-amber-300'],
              ['Pop', aggregate.total_population, 'text-slate-200'],
              ['Vuln', aggregate.total_vulnerable_population, 'text-sky-300'],
            ] as const
          ).map(([label, val, cls]) => (
            <div key={label}>
              <div className={`font-mono text-sm ${cls}`}>{val.toLocaleString()}</div>
              <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
            </div>
          ))}
        </dl>
        <p className="text-[10px] text-slate-500">
          {aggregate.community_count} linked{' '}
          {aggregate.community_count === 1 ? 'community' : 'communities'} ·{' '}
          {aggregate.total_warming_centres_active} warming{' '}
          {aggregate.total_warming_centres_active === 1 ? 'centre' : 'centres'} active
        </p>
        {/* #462 — the pending (not-yet-counted) banner: the incompleteness the rollup must
            never hide. Distinct from the #125 baseline note below (baseline = shown-but-
            flagged; pending = nothing recorded at all). */}
        {aggregate.pending_community_count > 0 && (
          <p className="text-[10px] text-amber-300" data-testid="evac-pending-note">
            {aggregate.pending_community_count} of {aggregate.community_count}{' '}
            {EVAC_ACCOUNTING_INCOMPLETE_PREFIX} — no headcount recorded (not zero).
          </p>
        )}
        {aggregate.baseline_headcount_count > 0 && (
          <p className="text-[10px] text-amber-300/80" data-testid="evac-baseline-note">
            {aggregate.baseline_headcount_count} of {aggregate.community_count} showing{' '}
            <span className="font-medium">baseline</span> (pre-incident) figures — no
            incident-tied headcount recorded yet.
          </p>
        )}
      </section>

      {/* (a) Linked communities */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Communities
          {communities.length > 0 && <span className="text-slate-500"> · {communities.length}</span>}
        </h3>
        {communities.length === 0 ? (
          <p className="text-sm text-slate-500">No communities linked to this incident yet.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="evac-communities">
            {communities.map((c) => {
              const ws = communityStatusStyle(
                (c.wellness_status as Parameters<typeof communityStatusStyle>[0]) ?? null,
              );
              return (
                <li
                  key={c.id}
                  className="rounded border border-slate-700 bg-slate-800/60 p-2 space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-medium truncate">{c.name}</span>
                      <span className="block text-[10px] text-slate-500">
                        {COMMUNITY_TYPE_LABEL[c.community_type] ?? 'Community'}
                        {c.population != null ? ` · pop ${c.population.toLocaleString()}` : ''}
                      </span>
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0"
                      style={{ backgroundColor: `${ws.color}22`, color: ws.color }}
                    >
                      {ws.label}
                    </span>
                    <button
                      type="button"
                      data-testid="evac-fly-community"
                      onClick={() => flyToCommunity(c)}
                      className="text-[10px] py-1 px-1.5 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 flex-shrink-0"
                    >
                      Fly to
                    </button>
                  </div>
                  {c.accounting_state === 'pending' ? (
                    // #462 — UNKNOWN ≠ ZERO: a zone community with no headcount recorded reads
                    // "Not yet counted", NEVER a grid of zeros that would imply an all-clear.
                    <p
                      className="text-[10px] text-amber-300 leading-tight rounded bg-amber-500/10 border border-amber-500/25 px-2 py-1"
                      data-testid="evac-community-pending"
                    >
                      {EVAC_ACCOUNTING_PENDING_LABEL} — no headcount recorded (not zero).
                    </p>
                  ) : (
                    <>
                      {c.accounting_state === 'baseline' && (
                        <p
                          className="text-[9px] text-amber-300/80 leading-tight"
                          data-testid="evac-community-baseline"
                          title="No incident-tied headcount recorded yet — showing the community's latest baseline snapshot."
                        >
                          baseline figures · not incident-specific
                        </p>
                      )}
                      <div className="grid grid-cols-4 gap-1 text-center">
                        {(
                          [
                            ['Evac', c.evacuated, 'text-rose-300'],
                            [EVAC_ACCOUNTING_REMAINING_SHORT, c.remaining, 'text-slate-200'],
                            ['Acct', c.accounted_for, 'text-emerald-300'],
                            [EVAC_ACCOUNTING_UNACCOUNTED_SHORT, c.unreachable, 'text-amber-300'],
                          ] as const
                        ).map(([label, val, cls]) => (
                          <div key={label}>
                            <div className={`font-mono text-xs ${cls}`}>{fmt(val)}</div>
                            <div className="text-[8px] uppercase tracking-wide text-slate-500">
                              {label}
                            </div>
                          </div>
                        ))}
                      </div>
                      {c.awaiting_airlift != null && (
                        <p
                          className="text-[9px] text-indigo-200/90 leading-tight"
                          data-testid="evac-community-awaiting-airlift"
                        >
                          {c.awaiting_airlift.toLocaleString()}{' '}
                          {EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT.toLowerCase()}
                        </p>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* (c) Staging areas */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Staging Areas
          {staging_areas.length > 0 && (
            <span className="text-slate-500"> · {staging_areas.length}</span>
          )}
        </h3>
        {staging_areas.length === 0 ? (
          <p className="text-sm text-slate-500">No staging areas established on this incident.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="evac-staging">
            {staging_areas.map((s) => (
              <li
                key={s.id}
                className="flex items-center gap-2 rounded border border-slate-700 bg-slate-800/60 p-2"
              >
                <span className="text-base flex-shrink-0" aria-hidden>
                  📦
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{s.name}</span>
                  <span className="block text-[10px] text-slate-500">
                    {s.capacity_assets != null ? `${s.capacity_assets} assets` : ''}
                    {s.capacity_assets != null && s.capacity_personnel != null ? ' · ' : ''}
                    {s.capacity_personnel != null ? `${s.capacity_personnel} personnel` : ''}
                    {s.capacity_assets == null && s.capacity_personnel == null
                      ? 'capacity n/a'
                      : ''}
                  </span>
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 flex-shrink-0">
                  {s.assigned_request_count} staged
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* (d) Aviation bases */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Aviation Assets
          {aviation_bases.length > 0 && (
            <span className="text-slate-500"> · {aviation_bases.length}</span>
          )}
        </h3>
        {aviation_bases.length === 0 ? (
          <p className="text-sm text-slate-500">No aviation bases within {500} km.</p>
        ) : (
          <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="evac-bases">
            {aviation_bases.map((a) => {
              const ls = lifecycleStateStyle(a.lifecycle_state);
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => openAsset(a.id)}
                    className="w-full flex items-center gap-2 text-left p-1.5 rounded bg-slate-800/60 hover:bg-slate-700 border border-slate-700"
                  >
                    <span className="text-base flex-shrink-0" aria-hidden>
                      🛩️
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs truncate">{a.name}</span>
                      <span className="block text-[10px] text-slate-400">
                        {a.distance_km.toFixed(0)} km {a.bearing}
                      </span>
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0"
                      style={{ backgroundColor: `${ls.color}22`, color: ls.color }}
                    >
                      {ls.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* (d) Live aircraft — airlift hulls highlighted */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Live Aircraft
          {aircraft.length > 0 && (
            <span className="text-slate-500">
              {' '}
              · {aircraft.length}
              {airliftCount > 0 ? ` · ${airliftCount} airlift` : ''}
            </span>
          )}
        </h3>
        {aircraft.length === 0 ? (
          <p className="text-sm text-slate-500">No live aircraft within {500} km.</p>
        ) : (
          <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="evac-aircraft">
            {aircraft.map((ac) => (
              <li key={ac.id}>
                <button
                  type="button"
                  onClick={() => openAircraft(ac.id)}
                  className={`w-full flex items-center gap-2 text-left p-1.5 rounded border ${
                    ac.is_airlift
                      ? 'bg-indigo-500/10 border-indigo-500/40 hover:bg-indigo-500/20'
                      : ac.is_water_bomber
                        ? 'bg-orange-500/10 border-orange-500/40 hover:bg-orange-500/20'
                        : 'bg-slate-800/60 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs truncate">
                      {ac.callsign ?? ac.registration}
                      {ac.is_airlift && (
                        <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-indigo-500/30 text-indigo-200">
                          airlift
                        </span>
                      )}
                      {ac.is_water_bomber && (
                        <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-orange-500/30 text-orange-200">
                          water bomber
                        </span>
                      )}
                    </span>
                    <span className="block text-[10px] text-slate-400">
                      {ac.type} · {ac.distance_km.toFixed(0)} km
                      {ac.altitude_ft != null ? ` · ${ac.altitude_ft.toLocaleString()} ft` : ''}
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-500 flex-shrink-0">
                    {ac.last_seen_seconds_ago < 60
                      ? `${ac.last_seen_seconds_ago}s ago`
                      : `${Math.floor(ac.last_seen_seconds_ago / 60)}m ago`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* (e) Overlapping CAP alerts */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Public Alerts (CAP) Over Communities
          {cap_alerts.length > 0 && <span className="text-slate-500"> · {cap_alerts.length}</span>}
        </h3>
        {cap_alerts.length === 0 ? (
          <p className="text-sm text-slate-500">No active public alerts over the linked communities.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="evac-cap-alerts">
            {cap_alerts.map((a) => (
              <li
                key={a.id}
                className="rounded border border-amber-500/30 bg-amber-500/5 p-2 space-y-0.5"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-amber-200 truncate">
                    {a.event ?? 'Alert'}
                  </span>
                  {a.severity && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 flex-shrink-0">
                      {a.severity}
                    </span>
                  )}
                </div>
                {a.headline && <p className="text-[11px] text-slate-300 leading-snug">{a.headline}</p>}
                <p className="text-[10px] text-slate-500">
                  {a.area_desc ?? ''}
                  {a.area_desc && a.expires_at ? ' · ' : ''}
                  {relExpiry(a.expires_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Assembled from linked-community headcounts (§4.25), staging areas (§4.18), the
        response-asset registry + live ADS-B (§4.5/§4.6), and overlapping public alerts.
        Read-only — coordinate the airlift from the resource lifecycle on the incident.
      </p>
    </div>
  );
}
